import { fetchAuthSession } from 'aws-amplify/auth';
import AWS, { CognitoIdentityCredentials } from 'aws-sdk';
import { AwsClient } from 'aws4fetch';
import { BaseNetworkRequestProp, getFetchSignal } from './fetch-core.ts';

const region = import.meta.env.VITE_AWS_REGION as string;

const credentials = new CognitoIdentityCredentials({
  AccountId: import.meta.env.VITE_AWS_ACCOUNT_ID as string,
  IdentityPoolId: import.meta.env
    .VITE_UNAUTHENTICATED_IDENTITY_POOL_ID as string,
});

const getAWSUnauthenticatedCredentials = async (): Promise<
  AWS.CognitoIdentityCredentials | undefined
> => {
  await credentials.getPromise();
  return credentials;
};

const getAccessToken = async () => {
  const { tokens } = await fetchAuthSession();
  return tokens?.accessToken?.toString() ?? '';
};

export async function makeAuthenticatedNetworkRequest({
  method,
  baseUrl,
  relativeUrl,
  body,
  signal,
  headers,
}: BaseNetworkRequestProp): Promise<Response> {
  const { credentials, tokens } = await fetchAuthSession();
  if (!credentials || !tokens) {
    throw new Error('Credentials not found');
  }

  const { accessKeyId, secretAccessKey, sessionToken } = credentials;
  const aws = new AwsClient({
    accessKeyId,
    secretAccessKey,
    sessionToken,
    region,
    service: 'execute-api',
  });

  const putHeaders =
    method === 'PUT'
      ? { 'access-token': await getAccessToken() }
      : ({} as Record<string, never>);

  return await aws.fetch(baseUrl + relativeUrl, {
    method: method,
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      'id-token': tokens?.idToken?.toString() ?? '',
      ...putHeaders,
      ...headers,
    },
    signal: getFetchSignal(signal),
  });
}

export async function makeUnauthenticatedNetworkRequest({
  method,
  baseUrl,
  relativeUrl,
  body,
  signal,
  headers,
}: BaseNetworkRequestProp): Promise<Response> {
  const { accessKeyId, secretAccessKey, sessionToken } =
    (await getAWSUnauthenticatedCredentials())!;

  const aws = new AwsClient({
    accessKeyId,
    secretAccessKey,
    sessionToken,
    region,
    service: 'execute-api',
  });

  return await aws.fetch(baseUrl + relativeUrl, {
    method: method,
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    signal: getFetchSignal(signal),
  });
}
